/**
 * PipelineDashboard — Container component for the AI Pipeline admin section.
 *
 * Orchestrates all pipeline sub-panels by calling useAiPipeline() and passing
 * the appropriate state slices and action callbacks to each panel.
 *
 * Layout: vertical stack with consistent spacing between sections.
 * Wraps content in an ARIA live region (polite) so screen readers announce data updates.
 * All interactive controls are keyboard navigable (Tab + Enter/Space).
 *
 * Requirements: 1.5, 9.5, 10.1, 10.2
 */

import React from 'react';
import { useAiPipeline } from '../../hooks/useAiPipeline';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { PipelineStatusPanel } from './PipelineStatusPanel';
import { MetricsPanel } from './MetricsPanel';
import { HealthPanel } from './HealthPanel';
import { ModelConfigPanel } from './ModelConfigPanel';
import { DlqPanel } from './DlqPanel';
import { RetriggerPanel } from './RetriggerPanel';

export function PipelineDashboard(): React.ReactElement {
  const { state, refreshAll, refreshStatus, refreshDlq, purgeDlq, retriggerPage } = useAiPipeline();

  // Safe fallbacks for Record<string, T> indexing (avoids boolean | undefined type errors)
  const statusLoading = state.isLoading['status'] ?? false;
  const statusError = state.errors['status'] ?? null;
  const metricsLoading = state.isLoading['metrics'] ?? false;
  const metricsError = state.errors['metrics'] ?? null;
  const healthLoading = state.isLoading['health'] ?? false;
  const healthError = state.errors['health'] ?? null;
  const configLoading = state.isLoading['config'] ?? false;
  const configError = state.errors['config'] ?? null;
  const dlqLoading = state.isLoading['dlq'] ?? false;
  const dlqError = state.errors['dlq'] ?? null;

  const isAnyLoading = Object.values(state.isLoading).some(Boolean);

  return (
    <section aria-labelledby="admin-ai-pipeline">
      {/* Section heading + Refresh All button */}
      <div className="mb-6 flex items-center justify-between">
        <h2 id="admin-ai-pipeline" className="text-2xl font-semibold leading-none tracking-tight">
          AI Pipeline
        </h2>
        <Button
          type="button"
          onClick={refreshAll}
          disabled={isAnyLoading}
          aria-label="Refresh all pipeline data"
        >
          {isAnyLoading ? (
            <>
              <span
                className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"
                aria-hidden="true"
              />
              Refreshing…
            </>
          ) : (
            'Refresh All'
          )}
        </Button>
      </div>

      {/* Data region wrapped in ARIA live region for polite updates */}
      <div aria-live="polite" aria-label="AI pipeline dashboard" className="space-y-6">
        {/* Pipeline Status */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Execution Status</h3>
          </CardHeader>
          <CardContent>
            <PipelineStatusPanel
              status={state.status}
              isLoading={statusLoading}
              error={statusError}
              onRefresh={refreshStatus}
            />
          </CardContent>
        </Card>

        {/* Metrics */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Pipeline Metrics</h3>
          </CardHeader>
          <CardContent>
            <MetricsPanel
              metrics={state.metrics}
              isLoading={metricsLoading}
              error={metricsError}
              onRetry={refreshAll}
            />
          </CardContent>
        </Card>

        {/* Health */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Health Status</h3>
          </CardHeader>
          <CardContent>
            <HealthPanel
              health={state.health}
              isLoading={healthLoading}
              error={healthError}
              onRetry={refreshAll}
            />
          </CardContent>
        </Card>

        {/* Model Configuration */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Model Configuration</h3>
          </CardHeader>
          <CardContent>
            <ModelConfigPanel
              config={state.config}
              isLoading={configLoading}
              error={configError}
              onRetry={refreshAll}
            />
          </CardContent>
        </Card>

        {/* DLQ */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Dead Letter Queue</h3>
          </CardHeader>
          <CardContent>
            <DlqPanel
              dlq={state.dlq}
              isLoading={dlqLoading}
              error={dlqError}
              onRefresh={refreshDlq}
              onPurge={purgeDlq}
            />
          </CardContent>
        </Card>

        {/* Retrigger */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium">Re-trigger Pipeline</h3>
          </CardHeader>
          <CardContent>
            <RetriggerPanel onRetrigger={retriggerPage} />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
